import { MdOutlineAdminPanelSettings, MdOutlineApartment, MdOutlineHistory, MdOutlineToggleOn } from 'react-icons/md'

import { HistoryDiff, HistoryField, HistoryFields, HistoryMuted } from '@/Ship/Components/History'

import { HISTORY_EVENT_MAP, isUserHistoryEvent } from '../Contracts/UserHistoryEvent'
import { roleLabel, statusLabel } from '../Contracts/UserOptions'

import type { UserHistoryItem } from '../Contracts/IUser'
import type { HistoryEntry } from '@/Ship/Components/History'
import type { ReactNode } from 'react'

/** Що змінює подія: поле (для пошуку у вкладеному `payload`) і його підпис. */
const EVENT_FIELD: Record<string, { name: string; label: string }> = {
    user_roles_changed: { name: 'roles', label: 'Ролі' },
    user_status_changed: { name: 'status', label: 'Статус' },
    user_departments_changed: { name: 'departments', label: 'Відділи' },
}

const ICONS: Record<string, ReactNode> = {
    user_roles_changed: <MdOutlineAdminPanelSettings />,
    user_status_changed: <MdOutlineToggleOn />,
    user_departments_changed: <MdOutlineApartment />,
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
    typeof value === 'object' && value !== null && !Array.isArray(value)

/**
 * Значення з `payload` — рядком для людини.
 *
 * Бек пише підписи (`SPEC-005` §3.5), але коди ролей і станів теж
 * перекладаємо: якщо в журнал потрапив код, людина все одно побачить
 * «Менеджер», а не `ROLE_MANAGER`. Відділ може прийти й об'єктом із назвою.
 */
const describe = (value: unknown, field: string): string => {
    const one = (item: unknown): string => {
        if (typeof item === 'string') {
            if (field === 'roles') return roleLabel(item)
            if (field === 'status') return statusLabel(item)
            return item
        }

        if (isRecord(item)) {
            const title = item['title'] ?? item['label'] ?? item['name']

            return typeof title === 'string' ? title : ''
        }

        return item === null || item === undefined ? '' : String(item)
    }

    const text = Array.isArray(value) ? value.map(one).filter(Boolean).join(', ') : one(value)

    return text !== '' ? text : '—'
}

const listOf = (value: unknown): unknown[] => (Array.isArray(value) ? value : [])

/**
 * «Було» і «стало». Бек кладе значення просто в `old` / `new`; вкладена
 * форма `{ old: { roles } }` і `{ from, to }` теж читаються — щоб стрічка не
 * спорожніла від зміни форми запису.
 */
const sides = (item: UserHistoryItem, field: string): { from: unknown; to: unknown } | null => {
    const payload = item.payload ?? {}
    const pick = (side: unknown): unknown => (isRecord(side) && field in side ? side[field] : side)

    if ('old' in payload || 'new' in payload) {
        // Немає «було» — це не «було порожньо», а «невідомо»: тоді лише нове
        return { from: 'old' in payload ? pick(payload.old) : undefined, to: pick(payload.new) }
    }

    if ('from' in payload || 'to' in payload) return { from: payload['from'], to: payload['to'] }

    return null
}

/** Подробиці події: що додано й знято, інакше — «було → стало». */
const details = (item: UserHistoryItem, field: { name: string; label: string }): ReactNode => {
    const added = listOf(item.payload?.added)
    const removed = listOf(item.payload?.removed)

    if (added.length > 0 || removed.length > 0) {
        return (
            <HistoryFields>
                {added.length > 0 ? (
                    <HistoryField label={field.label} action="added">
                        {describe(added, field.name)}
                    </HistoryField>
                ) : null}
                {removed.length > 0 ? (
                    <HistoryField label={field.label} action="removed">
                        {describe(removed, field.name)}
                    </HistoryField>
                ) : null}
            </HistoryFields>
        )
    }

    const change = sides(item, field.name)

    if (!change) return null

    if (change.from === undefined) return <span>{describe(change.to, field.name)}</span>

    return <HistoryDiff from={describe(change.from, field.name)} to={describe(change.to, field.name)} strike={false} />
}

/**
 * Події історії співробітника → рядки спільної стрічки `HistoryTimeline`.
 *
 * Невідомий код (бек додав подію, а фронт ще ні) не ховаємо: показуємо його
 * як є — так помітно, що мапу треба доповнити. Вартовий ловить це раніше.
 */
export const buildUserHistoryEntries = (items: readonly UserHistoryItem[]): HistoryEntry[] =>
    items.map((item, index) => {
        const info = isUserHistoryEvent(item.event) ? HISTORY_EVENT_MAP[item.event] : undefined
        const field = EVENT_FIELD[item.event]

        return {
            key: item.uuid ?? `${item.event}-${item.occurredAt ?? ''}-${index}`,
            title: info?.label ?? item.event,
            icon: ICONS[item.event] ?? <MdOutlineHistory />,
            tone: info?.tone ?? 'neutral',
            actor: item.changedBy ?? null,
            occurredAt: item.occurredAt,
            details: field ? details(item, field) : <HistoryMuted>{item.event}</HistoryMuted>,
        }
    })
